import { useAddGameParticipant } from '../participant'
import { useUpdateGame } from '../update'
import { toAddParticipant } from './transform'
import { type GameGetSuccess, type GameData, type GameId, type GameSetting } from '@terros/common'
import { useCallback, useState } from 'react'

type Return = {
  clone: (toGame: GameData, fromGame: GameGetSuccess) => Promise<void>
  error: string | undefined
  isCloning: boolean
}

export function useCloneParticipants(): Return {
  const [isCloning, setIsCloning] = useState(false)
  const { add: addParticipant, error: addParticipantError } = useAddGameParticipant()
  const { update: updateGame, error: updateGameError } = useUpdateGame()

  const cloneParticipants = useCallback(
    async (toGameId: GameId, fromGame: Pick<GameGetSuccess, 'participants'>) => {
      await addParticipant({
        gameId: toGameId,
        participants: fromGame.participants.map(toAddParticipant),
      })
    },
    [addParticipant]
  )

  const updateParticipantSettings = useCallback(
    async (game: GameData, settings: GameSetting) => {
      const participantSettings = getParticipantSettings(settings)
      await updateGame({
        ...game,
        settings: {
          ...game.settings,
          ...participantSettings,
        },
      })
    },
    [updateGame]
  )

  const clone = useCallback(
    async (toGame: GameData, fromGame: GameGetSuccess) => {
      try {
        setIsCloning(true)
        if (shouldUpdateParticipantSettings(toGame.settings, fromGame.game.settings)) {
          await updateParticipantSettings(toGame, fromGame.game.settings)
        }
        await cloneParticipants(toGame.gameId, fromGame)
      } finally {
        setIsCloning(false)
      }
    },
    [updateParticipantSettings, cloneParticipants]
  )

  return {
    clone,
    error: addParticipantError || updateGameError,
    isCloning,
  }
}

export const getParticipantSettings = (settings: GameSetting): GameSetting => {
  if (settings.participantType === 'user') {
    return {
      participantType: 'user',
    }
  }
  return {
    participantType: 'group',
    scoringPattern: settings.scoringPattern,
  }
}

const shouldUpdateParticipantSettings = (prevSettings: GameSetting, newSettings: GameSetting): boolean => {
  const prevType = prevSettings.participantType
  const newType = newSettings.participantType
  if (prevType !== newType) return true

  if (prevType === 'group' && newType === 'group') return prevSettings.scoringPattern !== newSettings.scoringPattern
  return false
}
